import { useRef, useState } from "react";

interface UseConfirmDialogStateOptions {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onConfirm: () => void | Promise<void>;
  onCancel?: () => void;
  loading: boolean;
}

export const useConfirmDialogState = ({
  open,
  onOpenChange,
  onConfirm,
  onCancel,
  loading,
}: UseConfirmDialogStateOptions) => {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const resolvedOpen = open ?? uncontrolledOpen;
  const [isConfirming, setIsConfirming] = useState(false);
  const [hasFailed, setHasFailed] = useState(false);
  const cancelRef = useRef<HTMLButtonElement>(null);

  // A parent can close and reopen a controlled dialog without going through `setOpen`, so a failure
  // from the previous opening is cleared whenever the dialog opens, not only when it closes.
  const [lastSeenOpen, setLastSeenOpen] = useState(resolvedOpen);
  if (resolvedOpen !== lastSeenOpen) {
    setLastSeenOpen(resolvedOpen);
    if (resolvedOpen) setHasFailed(false);
  }

  const isBusy = loading || isConfirming;

  const setOpen = (nextOpen: boolean) => {
    if (open === undefined) setUncontrolledOpen(nextOpen);
    onOpenChange?.(nextOpen);
  };

  // Radix only reports user-driven changes here (a trigger click, the Cancel button, Escape); a
  // successful confirm closes through `setOpen` directly, so it never counts as a cancel.
  const handleUserOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) onCancel?.();
    setOpen(nextOpen);
  };

  const confirm = () => {
    setHasFailed(false);
    let result: void | Promise<void>;
    try {
      result = onConfirm();
    } catch {
      setHasFailed(true);
      return;
    }
    if (!(result instanceof Promise)) {
      setOpen(false);
      return;
    }
    setIsConfirming(true);
    result
      .then(
        () => setOpen(false),
        () => setHasFailed(true)
      )
      .finally(() => setIsConfirming(false));
  };

  const focusCancel = (event: Event) => {
    event.preventDefault();
    cancelRef.current?.focus();
  };

  const blockEscapeWhileBusy = (event: KeyboardEvent) => {
    if (isBusy) event.preventDefault();
  };

  return {
    open: resolvedOpen,
    handleUserOpenChange,
    isBusy,
    hasFailed,
    confirm,
    cancelRef,
    focusCancel,
    blockEscapeWhileBusy,
  };
};
